/**
 * Fonte ÚNICA de rótulo do desfecho de um contato num disparo.
 *
 * Existe porque o MESMO vocabulário aparece em várias superfícies — colunas do kanban, linhas do
 * funil, KPIs e painéis de problema do detalhe do disparo, o wizard de nova conversa e o toast do
 * envio — e antes cada uma tinha dicionário próprio (o do `useBulkSendResultToast` cobria só parte
 * dos motivos, e o resto chegava cru na tela). Explicação e ação de cada desfecho moram ao lado,
 * em `dispatchOutcomeCopy.ts`; rótulo continua sendo só daqui.
 *
 * **Não rotula resposta.** `button:<rótulo>` e tipo cru continuam de
 * `replyButtonLabel` (`@/lib/dispatches`) e de `REPLY_KINDS_COM_COLUNA`
 * (`.../kanban/classify.ts`): dois donos pro mesmo rótulo foi exatamente como
 * donut e kanban passaram a contar o mesmo disparo de dois jeitos.
 */

import type { IDispatchProtocol } from '@/types/Dispatch'
import { motivoDaFalha } from './metaErrorCopy'

/**
 * Os DOZE motivos de `DispatchSkipReason`
 * (`app/domains/company/whatsapp/dispatches/skipped.py`, back `a570061b`), **na
 * ordem do enum do back**: os 3 síncronos do worker primeiro, os 3 de rota
 * depois, os 3 eixos de estado do contato — `contato_bloqueado` (arco A2),
 * `contato_deletado` (arco A3) e `contato_inalcancavel` (arco A4) —,
 * `opt_out_marketing` (`skipped.py:60`, épico erros-meta bloco 03) e, por
 * último, `parametro_vazio`/`corpo_excede_limite` (`skipped.py:66-67`): pulo
 * pelo OVERRIDE de parâmetro do contato, decidido na rota. A
 * ordem é carregada — ela decide a ordem dos motivos no toast de resumo e a
 * ordem das colunas de pulado no kanban.
 *
 * Quando o contato está em mais de um estado sai UM motivo só, pela precedência
 * **`deletado → bloqueado → opt-out de marketing → inalcançável`**, resolvida no
 * back (`app/domains/company/contacts/sendability.py::lot_skip_reason`) e em
 * nenhum lugar daqui. Repare que a precedência NÃO é a ordem de declaração:
 * deletado decide primeiro, mas aparece em 8º. As duas ordens são independentes
 * e ambas vêm do back.
 *
 * **Só `contato_inalcancavel` tem chave de desligar** (`block_unreachable_sending`,
 * `/settings/sending`); deletado e bloqueado pulam sempre, e `opt_out_marketing`
 * pula só em template de categoria MARKETING (utilidade/autenticação seguem
 * normais) — decisão do CONTATO, sem toggle de tenant. O motor do lote continua
 * no back.
 */
export const SKIP_REASON_LABELS: Record<string, string> = {
  em_atendimento_outro_canal: 'Em atendimento em outro canal',
  sem_acesso_ao_canal: 'Sem acesso ao canal',
  falha_no_envio: 'Falha no envio',
  sem_identificador: 'Sem telefone nem ID do WhatsApp',
  falha_ao_enfileirar: 'Falha ao enfileirar',
  contato_inexistente: 'Contato não existe mais',
  contato_bloqueado: 'Contato bloqueado',
  /* "Lixeira" e não "deletado": mesmo vocabulário da vista `?state=deleted`
     (`ContactsBrowser`) e de `GOVERNANCE_MESSAGES.CONTACT_INACTIVE`. Não
     explica como reverter porque deletado é SAÍDA do lote — não há toggle de
     tenant nem permission que traga o contato de volta ao disparo; o rótulo
     afirma só ONDE o contato está. Distinto de `contato_inexistente`, em que o
     registro sumiu de vez. */
  contato_deletado: 'Contato na lixeira',
  /* **Nunca "não está no WhatsApp"** (faixa 9 §0): o código 131026 da Meta cobre
     TRÊS causas — número não registrado, Termos não aceitos, app desatualizado — e
     o payload não diz qual. O rótulo afirma só o que é observável: o último envio
     não foi entregue. */
  contato_inalcancavel: 'Contato inalcançável (último envio não entregue)',
  /* Decisão do CONTATO, nunca falha nem bloqueio: só templates de MARKETING
     ficam de fora (contrato erros-meta §Opt-out 2). O 131050 síncrono chega com
     `skip_detail` `erro_meta` e o MESMO rótulo — o detalhe não troca o motivo. */
  opt_out_marketing: 'Contato não aceita marketing',
  /* Só o override DO contato (`contacts[].params`) pula; o mesmo defeito no
     valor global é 422 na criação e nem vira disparo. */
  parametro_vazio: 'Parâmetro vazio',
  corpo_excede_limite: 'Mensagem longa demais',
}

/** Prefixo do `outcome` de pulado no envelope de `/protocols` (`skipped:<reason>`). */
export const SKIPPED_PREFIX = 'skipped:'

/** Chave de `failed_breakdown` quando a Meta não mandou código (`failed_breakdown_key`). */
export const FAILED_BREAKDOWN_UNKNOWN = 'unknown'

/** Rótulo ÚNICO da falha sem código — chave `unknown` do funil e chip/célula das Falhas. Copy nunca diz "Meta". */
export const ERRO_SEM_INFORMACAO = 'Erro não informado'

/** Rótulo do CONTADOR agregado `counters.skipped` (não é um `outcome` — pulado
 *  só existe por motivo). Vive aqui pra que card, tabela e detalhe digam o mesmo. */
export const SKIPPED_TOTAL_LABEL = 'Não enviados'

/** Rótulo de um motivo CRU. Motivo que esta versão não conhece volta cru: coluna
 *  com nome feio é recuperável, contato invisível não. */
export function skipReasonLabel(reason: string): string {
  return SKIP_REASON_LABELS[reason] ?? reason
}

/**
 * O MESMO rótulo em caixa de meio de frase ("…: 2 em atendimento em outro canal").
 * Só a primeira letra muda — `toLowerCase()` na frase inteira comeria a sigla de
 * "Sem telefone nem ID do WhatsApp".
 */
export function skipReasonPhrase(reason: string): string {
  const label = skipReasonLabel(reason)
  return label.charAt(0).toLowerCase() + label.slice(1)
}

/** `skipped:<reason>` → `<reason>`. Qualquer outro `outcome` volta intacto. */
export function stripSkippedPrefix(outcome: string): string {
  return outcome.startsWith(SKIPPED_PREFIX) ? outcome.slice(SKIPPED_PREFIX.length) : outcome
}

/** Os cinco desfechos de ENTREGA. Plural porque é título de coluna e de KPI. */
const OUTCOME_LABELS: Record<string, string> = {
  sent: 'Enviados',
  delivered: 'Entregues',
  read: 'Lidos',
  failed: 'Falhas',
  /* "Sem retorno" e não "Sem status": `no_status` afirma só que a Meta nunca
     confirmou o envio — `delivered`/`read`/`replied` não entram na subtração que
     o produz, então prometer "nada aconteceu" seria mentira. */
  no_status: 'Sem retorno',
}

/**
 * O MESMO desfecho no SINGULAR — o estado de UM contato, não o título de uma
 * coluna. Irmão de `skipReasonPhrase`: mesma fonte, segunda forma, e por isso
 * mora aqui em vez de virar um segundo dicionário na tela.
 *
 * Existe porque a listagem de "Não entregues" precisa dizer o estado DENTRO da
 * linha, e "Enviados" numa linha de uma pessoa é erro de concordância que faz o
 * operador desconfiar do número ao lado. Motivo de pulado já é singular
 * (`SKIP_REASON_LABELS`) e volta por `skipReasonLabel`.
 */
const OUTCOME_STATE_LABELS: Record<string, string> = {
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
  /* Igual ao plural: "Sem retorno" já é invariável, e inventar uma variação só
     pra ter uma entrada diferente seria duas palavras pro mesmo fato. */
  no_status: 'Sem retorno',
}

export function outcomeStateLabel(outcome: string): string {
  if (outcome.startsWith(SKIPPED_PREFIX)) return skipReasonLabel(stripSkippedPrefix(outcome))
  return OUTCOME_STATE_LABELS[outcome] ?? outcomeLabel(outcome)
}

/**
 * Rótulo de um `outcome` de card ou de coluna. `skipped:<reason>` é resolvido
 * pelo motivo; chave de resposta volta INTACTA (não é deste módulo).
 */
export function outcomeLabel(outcome: string): string {
  if (outcome.startsWith(SKIPPED_PREFIX)) return skipReasonLabel(stripSkippedPrefix(outcome))
  return OUTCOME_LABELS[outcome] ?? outcome
}

/**
 * Motivo da falha, em cascata — o TEXTO é de `motivoDaFalha` ([[metaErrorCopy]]), aqui só a forma:
 * **copy própria do código → causa (do back ou derivada do código) + ` · <código>` → `error_title` →
 * `Erro <código>` → `null`**. O código vai junto do texto da causa porque dois códigos da mesma causa são
 * problemas distintos e o texto sozinho não os separa. `null` só sem código, sem causa e sem título — aí a
 * tela não mostra sub-rótulo em vez de inventar um.
 *
 * `error_title` vem em INGLÊS (é o título da Meta) e por isso é fallback, nunca primário.
 */
export function metaErrorLabel(
  code: number | null | undefined,
  title: string | null | undefined,
  cause?: string | null,
): string | null {
  const motivo = motivoDaFalha({ codigo: code, causa: cause })
  if (motivo.origem === 'codigo') return motivo.rotulo
  if (motivo.origem === 'causa') {
    return motivo.codigo === undefined ? motivo.rotulo : `${motivo.rotulo} · ${motivo.codigo}`
  }
  if (title && title.trim()) return title
  if (motivo.codigo !== undefined) return `Erro ${motivo.codigo}`
  return null
}

/** Rótulo de uma CHAVE de `failed_breakdown` — string, não number (`"131047"` | `"unknown"`). */
export function failedBreakdownLabel(key: string): string {
  if (key === FAILED_BREAKDOWN_UNKNOWN) return ERRO_SEM_INFORMACAO
  const code = Number(key)
  return metaErrorLabel(Number.isFinite(code) ? code : null, null) ?? key
}

const CHAVES_DE_STATUS = ['sent', 'delivered', 'read', 'failed'] as const
type ChaveDeStatus = (typeof CHAVES_DE_STATUS)[number]

function ehChaveDeStatus(outcome: string): outcome is ChaveDeStatus {
  return (CHAVES_DE_STATUS as readonly string[]).includes(outcome)
}

/**
 * Instante (epoch em SEGUNDOS) da transição que decidiu o `outcome` do card — a coluna "Quando" dos
 * painéis Falhas e Não entregues.
 *
 * `outcome_at` do back (2026-10-17) primeiro; sem ele (back velho, ou `null`), `template_status[outcome]`
 * — que o card sempre trouxe e é exatamente o instante da transição nos quatro status. `null` quando
 * nenhum dos dois tem valor positivo: o `0` explícito do banco significa "não aconteceu", e mostrá-lo
 * seria 1970.
 */
export function instanteDoDesfecho(
  card: Pick<IDispatchProtocol, 'outcome' | 'outcome_at' | 'template_status'>,
): number | null {
  const doBack = card.outcome_at
  if (doBack !== undefined && doBack !== null) return doBack > 0 ? doBack : null
  const status = card.template_status
  const valor = status && ehChaveDeStatus(card.outcome) ? status[card.outcome] : null
  return typeof valor === 'number' && valor > 0 ? valor : null
}
